import { useEffect } from "react";
import { Link } from "react-router-dom";
import { usePaymentsOpen, EARLY_ACCESS_LABEL } from "../lib/billingStatus";
import { ArrowRight, Check } from "lucide-react";
import { ChalkLogoLockup } from "../components/chalk/Logo";
import { LegalFooter } from "../components/chalk/LegalLayout";

const C = {
  board: "#1a3a2a", white: "rgba(255,255,255,0.92)", green: "#2E7D52",
  greenChalk: "#4db87a", cream: "#f5f0e8", offWhite: "#faf8f4",
  border: "rgba(0,0,0,0.08)", muted: "#5a5a5a", textDark: "#1a1a1a",
};
const DISPLAY = "'Bebas Neue', Oswald, Impact, sans-serif";
const MARKER  = "'Permanent Marker', Caveat, cursive";
const BODY    = "'Barlow', system-ui, sans-serif";

// ── Tier feature lists — kept in one place so marketing copy stays consistent.
// Each list emphasizes user-visible value (not implementation).
const TIERS = [
  {
    key: "pickup",
    name: "Pickup",
    price: "Free",
    body: "One team. The basics. Always free for casual coaches.",
    cta: "Start free",
    features: [
      "1 active team, up to 15 players",
      "Season schedule with location, time and weather",
      "One-tap RSVPs (going / can't go)",
      "Erase and Redraw one event at a time: cancel, delay or move, and every family is notified",
      "Conflict warnings when a new or moved event overlaps another",
      "Chalk Talk announcements and polls, with push and email alerts",
      "30 days of Chalk Talk history",
      "Game-day and volunteer-duty reminders for families",
      "1 team staff member (Team Mom, Dad or assistant)",
      "100 MB of team photos",
      "Season wrap-up with your roster carried into next season",
    ],
  },
  {
    key: "clipboard_plus",
    name: "The Clipboard+",
    price: "$7.99",
    interval: "/month",
    body: "For parents juggling kids across multiple teams. Never double-book your kids again.",
    cta: "Upgrade",
    features: [
      "Family Sports Dashboard: every child, every team, every event in one view",
      "Conflict detection: real time overlaps between your kids' events, shown side by side",
      "Full Chalk Talk history across all your teams",
      "Calendar subscription for Apple, Google or Outlook",
      "Add teams whose coach isn't on Chalk OS yet",
      "Per-event-type alert controls",
    ],
  },
  {
    key: "chalkboard",
    name: "The Chalkboard",
    price: "$11.99",
    interval: "/month",
    body: "The full coaching workbench. Plans, drafts, and a team you can wrangle.",
    cta: "Get Chalkboard",
    recommended: true,
    features: [
      "Everything in Pickup",
      "Bulk Erase and Redraw: cancel, delay or shift every event in a date range, with one combined alert per family",
      "Draw It Up AI: draft Chalk Talk in your voice",
      "Recurring events (weekly practices)",
      "Volunteer duties & one-tap snack rotation",
      "Audience targeting (whole team / coaches only / chosen families)",
      "Multiple guardians per athlete",
      "Full Chalk Talk history for your team",
      "RSVP late status and notes",
      "Up to 3 team staff, 5 pinned posts, 5 GB of photos",
      "Roster export",
    ],
  },
  {
    key: "league_os",
    name: "League OS",
    price: "Contact us",
    body: "For directors running 10+ teams under one umbrella.",
    cta: "Talk to us",
    sales: true,
    features: [
      "The Chalkboard's coach tools for every league team",
      "League-wide broadcasts to every team at once",
      "League overview: teams, athletes and upcoming events",
      "Set up with you directly by our team",
    ],
  },
];

export default function Pricing() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Pricing — ChalkOS · Simple plans for coaches, families, leagues";
    return () => { document.title = prev; };
  }, []);

  return (
    <div className="chalk-min-h-screen" data-testid="pricing-page" style={{ fontFamily: BODY, color: C.textDark, background: C.cream }}>
      <a href="#main" className="chalk-skip-link" data-testid="skip-link">Skip to main content</a>
      <header style={{ padding: "16px 22px", borderBottom: `1px solid ${C.border}`, background: C.offWhite }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", maxWidth: 1200, margin: "0 auto", gap: 16 }}>
          <Link to="/" style={{ textDecoration: "none" }} aria-label="Chalk OS home">
            <ChalkLogoLockup size={40} color={C.textDark} />
          </Link>
          <Link to="/login?mode=signup" className="chalk-btn-athletic chalk-btn-athletic--dark" style={{ padding: "11px 18px" }}>
            Get Chalk OS
          </Link>
        </div>
      </header>
      <main id="main" tabIndex={-1} style={{ padding: "72px 22px 96px", maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center" }}>
          <h1 style={{
            fontFamily: DISPLAY, fontWeight: 400,
            fontSize: "clamp(2.8rem, 5vw, 4.2rem)",
            lineHeight: 0.95, letterSpacing: "0.02em",
            textTransform: "uppercase",
            margin: 0, color: C.textDark,
          }}>Simple pricing.</h1>
          <div style={{
            fontFamily: MARKER, fontSize: "clamp(1.6rem, 3.2vw, 2.4rem)",
            color: C.green, margin: "8px 0 0", lineHeight: 1,
          }}>Start free. Upgrade when ready.</div>
          <span style={{ display: "block", width: 160, height: 3, background: C.green, margin: "18px auto 0", borderRadius: 2 }} />
          <p style={{ color: C.muted, fontSize: 16, marginTop: 18, fontFamily: BODY }}>
            Start free. Upgrade when your season ramps up.
          </p>
        </div>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 16, marginTop: 56, alignItems: "stretch",
        }}>
          {TIERS.map((t) => <PriceCard key={t.key} {...t} />)}
        </div>
        <p style={{ textAlign: "center", color: C.muted, fontSize: 12, marginTop: 32, fontFamily: BODY, letterSpacing: "0.06em" }}>
          No credit card required. Cancel anytime.
        </p>
      </main>
      <LegalFooter />
    </div>
  );
}

function PriceCard({ key: _k, name, price, interval, body, cta, recommended, sales, features }) {
  const open = usePaymentsOpen();
  const early = (cta === "Upgrade" || cta === "Get Chalkboard") && !open;
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <div
      data-testid={`pricing-${slug}`}
      className="chalk-marketing-card"
      style={{
        background: recommended ? "#1f3b2d" : C.offWhite,
        color: recommended ? C.white : C.textDark,
        border: recommended ? "none" : `1px solid ${C.border}`,
        padding: 26,
        borderRadius: 16,
        boxShadow: recommended ? "0 22px 50px rgba(26,58,42,0.32)" : "none",
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {recommended && (
        <span
          data-testid="pricing-recommended-badge"
          style={{
            position: "absolute", top: -12, left: 22,
            background: C.green, color: "#fff",
            fontFamily: BODY, fontWeight: 700, fontSize: 10,
            padding: "5px 10px", borderRadius: 6,
            letterSpacing: "0.16em", textTransform: "uppercase",
          }}
        >Recommended</span>
      )}
      <div style={{
        fontFamily: DISPLAY, fontSize: 26, letterSpacing: "0.02em",
        textTransform: "uppercase", lineHeight: 1,
      }}>{name}</div>
      <div style={{ marginTop: 12, display: "flex", alignItems: "baseline", gap: 6 }}>
        <span style={{ fontFamily: DISPLAY, fontSize: 48, letterSpacing: "0.02em", lineHeight: 1 }}>{price}</span>
        {interval && <span style={{ fontFamily: BODY, fontSize: 14, opacity: 0.7 }}>{interval}</span>}
      </div>
      <p style={{
        fontFamily: BODY, fontSize: 14, lineHeight: 1.55, marginTop: 12, marginBottom: 0,
        color: recommended ? "rgba(245,240,232,0.72)" : C.muted,
      }}>{body}</p>

      <FeatureList items={features} recommended={recommended} slug={slug} />

      <div style={{ flex: 1 }} />

      {sales ? (
        <a
          href="mailto:support@getchalkos.com?subject=League OS"
          data-testid={`pricing-cta-${slug}`}
          className="chalk-btn-athletic chalk-btn-athletic--dark"
          style={{ marginTop: 22, width: "100%" }}
        >
          {cta} <ArrowRight size={14} aria-hidden="true" />
        </a>
      ) : (
        <Link
          to="/login?mode=signup"
          data-testid={`pricing-cta-${slug}`}
          className={recommended ? "chalk-btn-athletic" : "chalk-btn-athletic chalk-btn-athletic--dark"}
          style={{
            marginTop: 22, width: "100%",
            background: recommended ? "#fff" : undefined,
            color: recommended ? C.board : undefined,
          }}
        >
          {early ? EARLY_ACCESS_LABEL : cta} <ArrowRight size={14} aria-hidden="true" />
        </Link>
      )}
      {early && <div data-testid={`pricing-early-note-${slug}`} style={{ fontFamily: BODY, fontSize: 12, marginTop: 10, opacity: 0.75 }}>Paid plans open soon.</div>}
    </div>
  );
}

function FeatureList({ items, recommended, slug }) {
  const accent = recommended ? "rgba(245,240,232,0.92)" : C.board;
  const checkBg = recommended ? "rgba(46,125,82,0.32)" : "rgba(46,125,82,0.12)";
  return (
    <ul
      data-testid={`pricing-features-${slug}`}
      style={{
        listStyle: "none", padding: 0, margin: "20px 0 4px",
        display: "flex", flexDirection: "column", gap: 10,
      }}
    >
      {items.map((it, i) => (
        <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
          <span
            aria-hidden="true"
            style={{
              flexShrink: 0, width: 18, height: 18, borderRadius: 999,
              background: checkBg, color: C.green,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              marginTop: 1,
            }}
          >
            <Check size={11} strokeWidth={3} />
          </span>
          <span style={{ fontSize: 13.5, lineHeight: 1.45, color: accent }}>
            {it}
          </span>
        </li>
      ))}
    </ul>
  );
}

const btnLight = {
  display: "inline-flex", alignItems: "center", gap: 6, justifyContent: "center",
  background: C.board, color: C.white, padding: "12px 16px", borderRadius: 9,
  fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em",
  textDecoration: "none", marginTop: 20, fontFamily: BODY,
};
const btnWhite = { ...btnLight, background: "#fff", color: C.board };
